import { useRef, useState, useMemo } from 'react';
import { Box, Button, Card, CardHeader, CardBody, Heading, HStack, VStack, Input, InputGroup, InputRightElement, IconButton, useDisclosure } from '@chakra-ui/react';
import { Plus, Search } from 'lucide-react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables';
import { useToastNotification } from '@/lib/utils/useToastNotification';
import { useAccessCheck } from '@/hooks/useAccess';
import { apiClient } from '@/lib/api/client';
import { useThemeColors } from '@/hooks/useThemeColors';
import BranchModal from '@/components/modals/AddBranchModal';
import { BranchType } from '@/types/index';

export default function BranchesPage() {
    const colors = useThemeColors();
    const tableRef = useRef<DataTableRef>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const { showError, showSuccess } = useToastNotification();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [searchQuery, setSearchQuery] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [totalTrash, setTotalTrash] = useState(0);
    const [editingBranch, setEditingBranch] = useState<BranchType | null>(null);

    const access = useAccessCheck();

    const statusFilterOptions = useMemo(
        () => [
            { value: '1', label: 'Active' },
            { value: '0', label: 'Inactive' },
        ],
        []
    );

    const tableOptions: DataTableOptions = useMemo(
        () => ({
            dataFields: {
                id: {
                    name: 'Branch ID',
                    maxWidth: '150px',
                },
                name: {
                    name: 'Branch Name',
                    maxWidth: '250px',
                },
                location: {
                    name: 'Location',
                    maxWidth: '200px',
                },
                description: {
                    name: 'Description',
                    maxWidth: '300px',
                },
                status: {
                    name: 'Status',
                    badge: {
                        '1': 'green',
                        '0': 'red',
                    },
                    badgeLabels: {
                        '1': 'Active',
                        '0': 'Inactive',
                    },
                },
            },
            dataSource: '/branches',
            primaryKey: 'id',
            softDelete: true,
            displayInModal: true,
            noRecordText: 'No branches found',
        }),
        [statusFilterOptions]
    );

    const permissions = useMemo(
        () => ({
            can_edit: access.can_edit,
            can_delete: access.can_delete,
            can_create: access.can_create,
            can_view: access.can_view,
            can_export: access.can_export,
            can_import: access.can_import,
        }),
        [access]
    );

    const showSubSubCallback = useMemo(
        () => (all: number, trashed: number) => {
            setTotalRows(all);
            setTotalTrash(trashed);
        },
        []
    );

    const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery]);

    const handleSearch = () => {
        const query = searchRef.current?.value || '';
        setSearchQuery(query);
        tableRef.current?.applyFilter('search', query);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleOpenModal = (branch?: BranchType) => {
        setEditingBranch(branch || null);
        onOpen();
    };

    const handleCloseModal = () => {
        setEditingBranch(null);
        onClose();
    };

    const handleSaveBranch = async (branchData: FormData) => {
        try {
            if (editingBranch) {
                branchData.append('_method', 'PUT');

                await apiClient.post(`/branches/${editingBranch.id}`, branchData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                showSuccess('Success', 'Branch updated successfully');
            } else {
                await apiClient.post('/branches', branchData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                showSuccess('Success', 'Branch created successfully');
            }
            tableRef.current?.reload();
            handleCloseModal();
        } catch (error: any) {
            showError(error);
        }
    };

    const handleEditBranch = (data: BranchType) => {
        apiClient
            .get(`/branches/${data.id}`)
            .then((response) => {
                handleOpenModal(response.data.data);
            })
            .catch((error) => {
                showError(error);
            });
    };

    return (
        <AdminLayout>
            <Box minH='calc(100vh - 110px)' overflow='hidden' display='flex' flexDirection='column' bg='#FEFCF8' py={1} px={5}>
                <VStack spacing={2} align='stretch' flex='1' overflow='hidden' minH={0}>
                    <Heading size='md' mb={1}>
                        Branches
                    </Heading>

                    <Box pt={2} pb={0}>
                        <HStack justify='flex-end' align='flex-end' spacing={4} pb={2} px={4} width='100%' bg='FEFCF8'>
                            <Box flex='0 1 auto' minW='250px' maxW='400px' bg='white'>
                                <InputGroup size='sm'>
                                    <Input ref={searchRef} placeholder='Search by name, location...' onKeyDown={handleKeyDown} defaultValue={searchQuery} pr='2.5rem' rounded='md' />
                                    <InputRightElement width='2.5rem'>
                                        <IconButton aria-label='Search' icon={<Search size={16} />} size='sm' h='1.75rem' w='1.75rem' onClick={handleSearch} variant='ghost' />
                                    </InputRightElement>
                                </InputGroup>
                            </Box>
                            <Box>
                                {permissions.can_create && (
                                    <Button py='6px' px='10px' bg='#DC3611' color='white' _hover={{ bg: '#bf3c1fff' }} onClick={() => handleOpenModal()} fontSize={{ base: 'xs', lg: 'sm' }} leftIcon={<Plus size={16} />} h='32px'>
                                        New Branch
                                    </Button>
                                )}
                            </Box>
                        </HStack>
                    </Box>

                    <Box h='100%' display='flex' flexDirection='column' minH={0}>
                        <DataTable ref={tableRef} options={tableOptions} params={tableParams} access={permissions} setSubSub={showSubSubCallback} onOpenModal={handleEditBranch} showCheckbox={false} />
                    </Box>
                </VStack>
            </Box>

            <BranchModal isOpen={isOpen} onClose={handleCloseModal} onSave={handleSaveBranch} editingBranch={editingBranch} />
        </AdminLayout>
    );
}
